import React, { useState, useEffect, useContext } from "react";
import {
  BookOpen,
  Plus,
  Search,
  Users,
  Star,
  TrendingUp,
  Calendar,
  X,
  Grid,
  List,
  Eye,
  Edit,
  Trash2,
  Clock,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Loader,
  DollarSign,
  Globe,
  Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AppContext from "../../../context/Appcontext";
import "../../../assets/css/admin/Courses.css";
import AdminLayout from "../../../layouts/AdminLayout/AdminLayout";

const Courses = () => {
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 8;
  const { baseUrl } = useContext(AppContext);

  // /*===================================  STATES  ===================================*/
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewMode, setViewMode] = useState("list"); // "list" | "grid"
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Fetch All Courses ─────────────────────────────────────────────────────
  const fetchCourses = () => {
    setLoading(true);
    setError(null);
    axios
      .get(`${baseUrl}/admin/course/viewAllCourses`)
      .then((res) => {
        console.log("viewAllCourses:", res.data);
        setCourses(res.data?.courses || res.data || []);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load courses. Please try again.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // ── Filter & Search ───────────────────────────────────────────────────────
  const filtered = courses.filter((c) => {
    const name = (c.courseName || c.title || "").toLowerCase();
    const status = (c.status || "").toLowerCase();
    const matchSearch = name.includes(searchQuery.toLowerCase());
    const matchFilter =
      filterStatus === "all" || status === filterStatus.toLowerCase();
    return matchSearch && matchFilter;
  });

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice(
    (safeCurrentPage - 1) * ITEMS_PER_PAGE,
    safeCurrentPage * ITEMS_PER_PAGE,
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // Reset to page 1 when filter/search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalStudents = courses.reduce(
    (a, c) => a + (parseInt(c.students) || parseInt(c.enrolled) || 0),
    0,
  );
  const avgRating = courses.length
    ? (
        courses.reduce((a, c) => a + (parseFloat(c.rating) || 0), 0) /
        courses.length
      ).toFixed(1)
    : "—";
  const publishedCount = courses.filter(
    (c) => (c.status || "").toLowerCase() === "published",
  ).length;

  const stats = [
    {
      label: "Total Courses",
      value: courses.length,
      icon: BookOpen,
      color: "blue",
      sub: `${publishedCount} published`,
    },
    {
      label: "Total Students",
      value: totalStudents.toLocaleString(),
      icon: Users,
      color: "green",
      sub: "Across all courses",
    },
    {
      label: "Avg. Rating",
      value: avgRating,
      icon: Star,
      color: "yellow",
      sub: "Overall quality score",
    },
    {
      label: "Active Courses",
      value: publishedCount,
      icon: TrendingUp,
      color: "purple",
      sub: "Live & enrolling",
    },
  ];

  // ── Modal ─────────────────────────────────────────────────────────────────
  const openModal = (type, course) => {
    setModalType(type);
    setSelectedCourse(course);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalType("");
    setSelectedCourse(null);
  };

  // ── DELETE — fixed ────────────────────────────────────────────────────────
  const handleDelete = () => {
    if (!selectedCourse) return;
    setDeleting(true);

    axios
      .delete(`${baseUrl}/admin/course/deleteCourse/${selectedCourse.id}`)
      .then(() => {
        setCourses((prev) => prev.filter((c) => c.id !== selectedCourse.id));
        closeModal();
      })
      .catch((deleteErr) => {
        console.warn("DELETE method failed, trying POST…", deleteErr);
        axios
          .post(`${baseUrl}/admin/course/deleteCourse/${selectedCourse.id}`)
          .then(() => {
            setCourses((prev) =>
              prev.filter((c) => c.id !== selectedCourse.id),
            );
            closeModal();
          })
          .catch((postErr) => {
            console.error("deleteCourse error:", postErr);
            const msg =
              postErr?.response?.data?.message ||
              postErr?.response?.data?.error ||
              "Failed to delete course.";
            alert(`❌ ${msg}`);
          })
          .finally(() => setDeleting(false));
        return;
      })
      .finally(() => {});
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getStatusClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "published") return "status-published";
    if (s === "draft") return "status-draft";
    return "status-pending";
  };

  const getThumbnail = (course) => {
    const raw = course.thumbnail || course.courseImage || course.image || null;
    if (!raw) return null;

    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;

    const serverBase = baseUrl.replace(/\/api\/?$/, "");

    return `${serverBase}/storage/${raw.replace(/^\//, "")}`;
  };

  // ── Pagination renderer ───────────────────────────────────────────────────
  const renderPagination = () => {
    const pages = [];
    const delta = 2;
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= safeCurrentPage - delta && i <= safeCurrentPage + delta)
      ) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...");
      }
    }
    return pages;
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <div className="crs-container">
        {/* ── Header ── */}
        <div className="crs-header">
          <div className="crs-header-left">
            <h1 className="crs-title">Course Catalog</h1>
            <p className="crs-subtitle">
              Manage, monitor and publish your courses
            </p>
          </div>
          <div className="crs-header-actions">
            <button
              className="crs-create-btn"
              onClick={() => navigate("/admin/create-course")}
            >
              <Plus size={18} />
              New Course
            </button>
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className="crs-stats-grid">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className={`crs-stat-card crs-stat-card--${s.color}`}
              >
                <div className="crs-stat-icon-wrap">
                  <Icon size={22} />
                </div>
                <div className="crs-stat-body">
                  <span className="crs-stat-label">{s.label}</span>
                  <span className="crs-stat-value">
                    {loading ? "—" : s.value}
                  </span>
                  <span className="crs-stat-sub">{s.sub}</span>
                </div>
                <div className="crs-stat-bg-icon">
                  <Icon size={64} />
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Toolbar ── */}
        <div className="crs-toolbar">
          <div className="crs-search-wrap">
            <Search size={16} className="crs-search-icon" />
            <input
              className="crs-search-input"
              type="text"
              placeholder="Search courses by name…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="crs-search-clear"
                onClick={() => setSearchQuery("")}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="crs-toolbar-right">
            <select
              className="crs-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="pending_review">Pending Review</option>
            </select>

            <div className="crs-view-toggle">
              <button
                className={`crs-view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                title="List view"
              >
                <List size={16} />
              </button>
              <button
                className={`crs-view-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                title="Grid view"
              >
                <Grid size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Results info ── */}
        <div className="crs-results-bar">
          <span className="crs-results-text">
            {loading
              ? "Loading…"
              : `Showing ${paginated.length} of ${filtered.length} courses`}
          </span>
          {filterStatus !== "all" && (
            <button
              className="crs-clear-filter"
              onClick={() => setFilterStatus("all")}
            >
              <X size={12} /> Clear filter
            </button>
          )}
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="crs-loading">
            <Loader size={32} className="crs-spinner" />
            <p>Loading courses…</p>
          </div>
        )}

        {/* ── Error ── */}
        {!loading && error && (
          <div className="crs-error">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button onClick={fetchCourses}>Retry</button>
          </div>
        )}

        {/* ── Empty ── */}
        {!loading && !error && filtered.length === 0 && (
          <div className="crs-empty">
            <BookOpen size={48} />
            <h3>No courses found</h3>
            <p>
              {searchQuery || filterStatus !== "all"
                ? "Try adjusting your filters"
                : "Create your first course to get started"}
            </p>
            {!searchQuery && filterStatus === "all" && (
              <button
                className="crs-create-btn"
                onClick={() => navigate("/admin/create-course")}
              >
                <Plus size={16} />
                Create Course
              </button>
            )}
          </div>
        )}

        {/* LIST VIEW */}
        {!loading && !error && filtered.length > 0 && viewMode === "list" && (
          <div className="crs-table-wrap">
            <table className="crs-table">
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Category</th>
                  <th>
                    <span className="crs-th-icon">
                      <Users size={13} />
                      Students
                    </span>
                  </th>
                  <th>
                    <span className="crs-th-icon">
                      <Star size={13} />
                      Rating
                    </span>
                  </th>
                  <th>
                    <span className="crs-th-icon">
                      <Clock size={13} />
                      Duration
                    </span>
                  </th>
                  <th>Status</th>
                  <th className="crs-th-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((course) => {
                  const thumb = getThumbnail(course);
                  return (
                    <tr key={course.id} className="crs-tr">
                      <td>
                        <div className="crs-course-cell">
                          <div className="crs-thumb">
                            {thumb ? (
                              <img
                                src={thumb}
                                alt={course.courseName || course.title}
                              />
                            ) : (
                              <BookOpen size={18} />
                            )}
                          </div>
                          <div>
                            <div className="crs-course-name">
                              {course.courseName || course.title || "Untitled"}
                            </div>
                            <div className="crs-course-date">
                              <Calendar size={11} />
                              {course.created_at
                                ? new Date(
                                    course.created_at,
                                  ).toLocaleDateString()
                                : "—"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="crs-category-tag">
                          {course.category || "—"}
                        </span>
                      </td>
                      <td className="crs-td-center">
                        <strong>
                          {course.students || course.enrolled || 0}
                        </strong>
                      </td>
                      <td>
                        <div className="crs-rating">
                          <Star size={13} className="crs-star" />
                          <span>
                            {course.rating
                              ? parseFloat(course.rating).toFixed(1)
                              : "—"}
                          </span>
                        </div>
                      </td>
                      <td className="crs-td-muted">
                        {course.courseDuration || course.duration || "—"}
                      </td>
                      <td>
                        <span
                          className={`crs-status ${getStatusClass(course.status)}`}
                        >
                          {course.status
                            ? course.status.charAt(0).toUpperCase() +
                              course.status.slice(1).replace("_", " ")
                            : "Draft"}
                        </span>
                      </td>
                      <td>
                        <div className="crs-actions">
                          <button
                            className="crs-act-btn crs-act-view"
                            onClick={() => openModal("view", course)}
                            title="View"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            className="crs-act-btn crs-act-edit"
                            onClick={() =>
                              navigate(`/admin/create-course?edit=${course.id}`)
                            }
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            className="crs-act-btn crs-act-del"
                            onClick={() => openModal("delete", course)}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* GRID VIEW */}
        {!loading && !error && filtered.length > 0 && viewMode === "grid" && (
          <div className="crs-grid">
            {paginated.map((course) => {
              const thumb = getThumbnail(course);
              return (
                <div key={course.id} className="crs-card">
                  <div className="crs-card-thumb">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={course.courseName || course.title}
                      />
                    ) : (
                      <div className="crs-card-thumb-placeholder">
                        <BookOpen size={32} />
                      </div>
                    )}
                    <span
                      className={`crs-card-status ${getStatusClass(course.status)}`}
                    >
                      {course.status
                        ? course.status.charAt(0).toUpperCase() +
                          course.status.slice(1).replace("_", " ")
                        : "Draft"}
                    </span>
                  </div>
                  <div className="crs-card-body">
                    <div className="crs-card-cat">
                      {course.category || "Uncategorized"}
                    </div>
                    <h3 className="crs-card-title">
                      {course.courseName || course.title || "Untitled"}
                    </h3>
                    <p className="crs-card-desc">
                      {(
                        course.courseDescription ||
                        course.description ||
                        "No description available."
                      ).slice(0, 90)}
                      {(course.courseDescription || course.description || "")
                        .length > 90
                        ? "…"
                        : ""}
                    </p>
                    <div className="crs-card-meta">
                      <span>
                        <Users size={12} />
                        {course.students || course.enrolled || 0}
                      </span>
                      <span>
                        <Star size={12} className="crs-star" />
                        {course.rating
                          ? parseFloat(course.rating).toFixed(1)
                          : "—"}
                      </span>
                      <span>
                        <Clock size={12} />
                        {course.courseDuration || course.duration || "—"}
                      </span>
                    </div>
                  </div>
                  <div className="crs-card-footer">
                    <button
                      className="crs-card-btn crs-card-btn--view"
                      onClick={() => openModal("view", course)}
                    >
                      <Eye size={14} />
                      View
                    </button>
                    <button
                      className="crs-card-btn crs-card-btn--edit"
                      onClick={() =>
                        navigate(`/admin/create-course?edit=${course.id}`)
                      }
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      className="crs-card-btn crs-card-btn--delete"
                      onClick={() => openModal("delete", course)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && !error && totalPages > 1 && (
          <div className="crs-pagination">
            <span className="crs-pagination-info">
              Page <strong>{safeCurrentPage}</strong> of{" "}
              <strong>{totalPages}</strong>
              &nbsp;·&nbsp;{filtered.length} total
            </span>
            <div className="crs-pagination-btns">
              <button
                className="crs-page-btn"
                onClick={() => handlePageChange(safeCurrentPage - 1)}
                disabled={safeCurrentPage === 1}
              >
                <ChevronLeft size={16} />
                Previous
              </button>

              {renderPagination().map((p, i) =>
                p === "..." ? (
                  <span key={`ellipsis-${i}`} className="crs-page-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`crs-page-num ${safeCurrentPage === p ? "active" : ""}`}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                ),
              )}

              <button
                className="crs-page-btn"
                onClick={() => handlePageChange(safeCurrentPage + 1)}
                disabled={safeCurrentPage === totalPages}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── Modal ── */}
        {modalOpen && (
          <div className="crs-modal-overlay" onClick={closeModal}>
            <div className="crs-modal" onClick={(e) => e.stopPropagation()}>
              <div className="crs-modal-header">
                <h2>
                  {modalType === "view" && "Course Details"}
                  {modalType === "delete" && "Delete Course"}
                </h2>
                <button className="crs-modal-close" onClick={closeModal}>
                  <X size={20} />
                </button>
              </div>

              <div className="crs-modal-body">
                {/* VIEW */}
                {modalType === "view" &&
                  selectedCourse &&
                  (() => {
                    const thumb = getThumbnail(selectedCourse);
                    return (
                      <div className="crs-view-modal">
                        {thumb && (
                          <img
                            src={thumb}
                            alt="thumbnail"
                            className="crs-modal-thumb"
                          />
                        )}
                        <div className="crs-modal-details">
                          {[
                            [
                              "Course Name",
                              selectedCourse.courseName || selectedCourse.title,
                            ],
                            ["Category", selectedCourse.category],
                            ["Level", selectedCourse.level],
                            ["Language", selectedCourse.language],
                            [
                              "Duration",
                              selectedCourse.courseDuration ||
                                selectedCourse.duration,
                            ],
                            [
                              "Price",
                              selectedCourse.price
                                ? `$${selectedCourse.price}`
                                : "Free",
                            ],
                            [
                              "Students",
                              selectedCourse.students ||
                                selectedCourse.enrolled ||
                                0,
                            ],
                            ["Rating", selectedCourse.rating || "—"],
                            ["Status", selectedCourse.status],
                            ["Slug", selectedCourse.slug],
                          ]
                            .filter(([, v]) => v)
                            .map(([label, value]) => (
                              <div key={label} className="crs-detail-row">
                                <span className="crs-detail-label">
                                  {label}
                                </span>
                                <span className="crs-detail-value">
                                  {label === "Status" ? (
                                    <span
                                      className={`crs-status ${getStatusClass(value)}`}
                                    >
                                      {value}
                                    </span>
                                  ) : (
                                    value
                                  )}
                                </span>
                              </div>
                            ))}
                        </div>
                        {(selectedCourse.courseDescription ||
                          selectedCourse.description) && (
                          <div className="crs-modal-desc">
                            <strong>Description</strong>
                            <p>
                              {selectedCourse.courseDescription ||
                                selectedCourse.description}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                {/* DELETE */}
                {modalType === "delete" && selectedCourse && (
                  <div className="crs-delete-modal">
                    <div className="crs-delete-icon">
                      <Trash2 size={32} />
                    </div>
                    <p>
                      Are you sure you want to delete{" "}
                      <strong>
                        "{selectedCourse.courseName || selectedCourse.title}"
                      </strong>
                      ?
                    </p>
                    <div className="crs-delete-warning">
                      <AlertCircle size={16} />
                      This action is permanent. All course data, student
                      progress, and content will be deleted.
                    </div>
                  </div>
                )}
              </div>

              <div className="crs-modal-footer">
                <button className="crs-modal-cancel" onClick={closeModal}>
                  Cancel
                </button>
                {modalType === "view" && (
                  <button className="crs-modal-confirm" onClick={closeModal}>
                    Close
                  </button>
                )}
                {modalType === "delete" && (
                  <button
                    className="crs-modal-danger"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <Loader size={14} className="crs-spinner" />
                        Deleting…
                      </>
                    ) : (
                      <>
                        <Trash2 size={14} />
                        Delete
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Courses;
